/**
 * `<RecordPage>` — one page for every record in Rowted.
 *
 * A contact, a property, a job, a visit, an invoice, a piece of equipment:
 * the same header, the same tabs, the same rail, the same Activity and Files.
 * What differs between them is a *configuration* — what the mark is, which
 * three numbers go in the pills, which tabs there are — and that is the whole
 * point. Rule 1 of the record page plan: "Build the shell once and configure
 * it per object; never fork it per module."
 *
 * Three layouts, one set of components (rule 3):
 *
 *   **Desktop ≥1180px** — a 340px rail beside the content.
 *   **iPad 768–1179** — a 280px rail, tighter gutters, scrolling tabs.
 *   **Phone <768** — one column, quick actions under the identity, a bottom
 *     tab bar, and the rail's cards stacked into the Overview.
 *
 * Nothing here knows what a contact is. The only thing it knows is that a
 * record has a name, some numbers, some tabs and some cards.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { AnyRecordType } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { usePhone } from '../lib/use-narrow.js'
import { IconChevronLeft } from '../frame/icons.js'
import { RecordHeader, type RecordAction, type RecordMeta, type RecordTag } from './record-header.js'
import { RecordTabs, type RecordTab } from './record-tabs.js'
import { RecordChromeProvider, useRecordChrome } from './record-chrome.js'
import { PhoneQuickActions, PhoneTabBar, type QuickAction } from './record-phone.js'
import type { MenuItem } from '../components/data-list.js'
import type { QuietSpec } from './record-quiet.js'
import type { RecordSummary } from '../lib/use-record.js'

export interface RecordConfig<T> {
  object: AnyRecordType
  /** Breadcrumbs: the module, then this record. */
  breadcrumb: (record: T) => { label: string; href?: string }[]
  /** 56px: an avatar, a map square, a status block. */
  mark: (record: T) => ReactNode
  title: (record: T) => string
  tags?: (record: T) => RecordTag[]
  /** One line, never wrapped — it loses items from the right instead. */
  meta: (record: T) => RecordMeta[]
  primary?: (record: T) => RecordAction | null
  /** Buttons beside the primary — Edit, and whatever else this record does. */
  actions?: (record: T) => RecordAction[]
  menu?: (record: T) => MenuItem[]
  tabs: (record: T, summary: RecordSummary | null) => RecordTab[]
  /** The cards beside the content: About, and whatever this record links to. */
  rail: (record: T, summary: RecordSummary | null) => ReactNode
  /**
   * What this record says when nothing is connected to it yet —
   * `docs/quiet-records.md` §2.
   *
   * The shell needs `when` for one reason and one reason only: **on a phone
   * the quiet panel comes before the rail's cards and the busy Overview comes
   * after them** (§0). A brand-new property whose first screen is About, Zone
   * — and Account — is a page that answers a question nobody asked before it
   * says what to do; a property with four hundred jobs on it is the other way
   * round, because the rail is then the summary and the content is the
   * detail. The predicate is the Overview's own (`isQuietSummary`), passed
   * here rather than recomputed, so the two orders cannot disagree.
   */
  quiet?: QuietSpec<T>
  /**
   * The visit page's header (folder 50) — opt-in and additive (lead decision
   * 13, `RecordHeaderProps.layout`). Left out, the header is today's.
   */
  headerLayout?: 'stacked'
  /** With `headerLayout: 'stacked'`: what the board draws under the header (Board 13 B). */
  belowHeader?: (record: T) => ReactNode
  /**
   * Folder 50's iPad gutters — 16 / 20 around the body and 16 between the rail
   * and the column (Board 15) — in place of the shell's 14. Opt-in; left out,
   * the body is today's.
   */
  tabletGutters?: 'wide'
  /**
   * Folder 52's buttons (`docs/job-stages.md` §1, Board 1): `RecordHeaderProps.buttons`,
   * per record (a job in part 2's statuses keeps today's header). Opt-in; left
   * out, or answering null, the header's buttons are today's.
   */
  headerButtons?: (record: T) => 'stages' | null
  phone?: {
    /**
     * Three fixed ones; the fourth is the green one for the open tab.
     *
     * It takes the open tab too, because a quick action can belong to a tab:
     * a property's **Measure** is only worth a slot while the Map tab is open
     * (`property-maps-v2.md` §6.1), and on every other tab that slot is
     * better spent on Navigate and Call.
     */
    quickActions: (record: T, tab: string) => QuickAction[]
    /** Which four tabs get a cell in the bottom bar. The fifth is More. */
    tabBar: string[]
    primaryByTab?: (tab: string, record: T) => QuickAction | null
    /** With `headerLayout: 'stacked'`: the line under the name (Board 16). */
    subline?: (record: T) => string
    /** With `headerLayout: 'stacked'`: the top row's pencil (Board 16); null draws none. */
    onEdit?: (record: T) => (() => void) | null
  }
}

export interface RecordPageProps<T> {
  config: RecordConfig<T>
  record: T
  summary: RecordSummary | null
  /** What each tab draws. The shell decides which one is open. */
  children: (tab: string) => ReactNode
}

/**
 * Which tab is open: the URL, or Overview.
 *
 * The URL carries it so a link to "this property's files" is a link somebody
 * can send. Nothing else does — **every record opens on Overview.**
 *
 * It used to remember the last tab per object type, on the theory that
 * somebody who works out of Activity wants Activity on the next contact too.
 * In use that theory is wrong and the cost is high: clicking through six
 * properties, every one of them opens on whatever tab the last one happened
 * to be left on, and the page you land on is a page you did not choose. A
 * record page that opens somewhere different each time is a page nobody can
 * build a habit on.
 *
 * Overview is the right landing every time because it is the tab that answers
 * "what is this and what is happening with it" — which is the question
 * somebody opening a record has, before they know what they want next.
 */

/**
 * §7: the chrome is foldable, so the shell is wrapped in the provider that
 * carries the fold. The provider is here rather than around the whole app
 * because a fold is per record page — unfolding is leaving the page.
 */
export function RecordPage<T>(props: RecordPageProps<T>) {
  return (
    <RecordChromeProvider>
      <RecordShell {...props} />
    </RecordChromeProvider>
  )
}

function RecordShell<T>({ config, record, summary, children }: RecordPageProps<T>) {
  const phone = usePhone()
  const chrome = useRecordChrome()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const tabs = useMemo(() => config.tabs(record, summary), [config, record, summary])

  const fromUrl = params.get('tab')
  const [tab, setTab] = useState(() => (
    fromUrl && tabs.some((candidate) => candidate.key === fromUrl)
      ? fromUrl
      : tabs[0]?.key ?? 'overview'))

  // A tab in the URL wins whenever it changes — that is what makes Back work.
  useEffect(() => {
    if (fromUrl && fromUrl !== tab && tabs.some((candidate) => candidate.key === fromUrl)) {
      setTab(fromUrl)
    }
  }, [fromUrl, tab, tabs])

  const openTab = (key: string) => {
    setTab(key)
    const next = new URLSearchParams(params)
    next.set('tab', key)
    setParams(next, { replace: true })
  }

  /*
   * Whether this record is quiet, asked once. Only the phone's card order
   * reads it (`RecordConfig.quiet`); the Overview asks the same question for
   * itself, from the same function.
   */
  const quietHere = config.quiet?.when(record, summary) ?? false
  /** The one place the phone's Overview puts the content above the rail. */
  const panelFirst = phone && tab === 'overview' && quietHere
  const quick = config.phone?.quickActions(record, tab) ?? []
  const green = config.phone?.primaryByTab?.(tab, record) ?? null

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/*
        * §7: folded, the header, its pills and the tab strip are one 44px bar
        * naming where you are and offering the way back. The tab strip goes
        * with the header on purpose — the point is the vertical room, and a
        * tab row under a slim bar is most of what was just folded away.
        */}
      {chrome.folded ? (
        <button
          type="button"
          onClick={chrome.unfold}
          className={cn(
            'flex h-11 shrink-0 items-center gap-1.5 border-b border-line bg-surface px-3',
            'text-cell font-semibold text-text hover:bg-hover',
          )}
        >
          <span className="inline-flex text-text-muted"><IconChevronLeft size={16} /></span>
          <span className="truncate">{config.title(record)}</span>
          <span className="shrink-0 text-text-faint">·</span>
          <span className="shrink-0 font-medium text-text-muted">
            {tabs.find((candidate) => candidate.key === tab)?.label ?? ''}
          </span>
        </button>
      ) : (
      <RecordHeader
        phone={phone}
        // The quick actions carry the rule when they are there.
        flush={phone && quick.length > 0}
        breadcrumb={config.breadcrumb(record)}
        mark={config.mark(record)}
        title={config.title(record)}
        tags={config.tags?.(record) ?? []}
        meta={config.meta(record)}
        pills={summary?.pills ?? []}
        primary={config.primary?.(record) ?? null}
        actions={config.actions?.(record) ?? []}
        menu={config.menu?.(record) ?? []}
        onBack={() => navigate(-1)}
        {...(config.headerLayout ? { layout: config.headerLayout } : {})}
        {...(config.headerButtons?.(record) ? { buttons: config.headerButtons(record)! } : {})}
        {...(config.belowHeader ? { below: config.belowHeader(record) } : {})}
        {...(phone && config.phone?.subline ? { subline: config.phone.subline(record) } : {})}
        {...(phone && config.phone?.onEdit?.(record) ? { onEdit: config.phone.onEdit(record)! } : {})}
      />
      )}

      {!chrome.folded && phone && quick.length > 0 && (
        <PhoneQuickActions actions={quick} green={green} />
      )}

      {!phone && !chrome.folded && (
        <RecordTabs tabs={tabs} active={tab} onChange={openTab} tabletGutter={config.tabletGutters === 'wide'} />
      )}

      {/*
        * The body. On a desktop the rail is a column beside it; on a phone the
        * rail's cards are simply the first thing in the Overview, because a
        * 340px column does not exist there and a card is a card.
        */}
      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto bg-bg',
          phone ? 'px-4 pb-28 pt-3'
            : config.tabletGutters === 'wide'
              ? 'flex gap-4 px-5 pb-6 pt-4 xl:gap-5 xl:px-6 xl:py-5'
              : 'flex gap-3.5 p-3.5 xl:gap-5 xl:px-6 xl:py-5',
        )}
      >
        {/*
          * The rail never shrinks (record page §2): it is 280px on an iPad
          * and 340px on a desktop, and the main column takes what is left.
          * A rail that gave way would squeeze an About card into two columns
          * of ellipsis.
          */}
        {!phone && !tabs.find((candidate) => candidate.key === tab)?.wide && (
          <aside className="flex w-rail-tablet shrink-0 flex-col gap-3.5 xl:w-rail">
            {config.rail(record, summary)}
          </aside>
        )}

        <main className="flex min-w-0 flex-1 flex-col gap-3.5">
          {/*
            * On a phone the rail is not a column — it is the first cards of
            * the Overview. Same components, folded rather than replaced.
            *
            * …except while the record is quiet, when the panel goes first.
            * See `RecordConfig.quiet` for why: a record with nothing on it
            * has nothing for About to be the summary *of*, so a phone that
            * opens on it shows four dashes and a Contacts card reading
            * "Nobody is on this property yet" before it offers anything to
            * do about either.
            */}
          {panelFirst && children(tab)}
          {phone && tab === 'overview' && config.rail(record, summary)}
          {!panelFirst && children(tab)}
        </main>
      </div>

      {phone && config.phone && (
        <PhoneTabBar
          tabs={tabs}
          cells={config.phone.tabBar}
          active={tab}
          onChange={openTab}
          objectLabel={config.object}
          {...(config.headerLayout === 'stacked' ? { moreSheet: 'list' as const } : {})}
        />
      )}
    </div>
  )
}
